import { useCallback, useEffect, useState } from 'react';

/**
 * Provider Admin: mounted SFTP / file-share inbox paths for an Originator org.
 * Paths are derived from organisation {@code code} on the server mount.
 */
export function OrganizationIngestChannelsCard({ org, loadChannels }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!org?.id || org.org_type !== 'originator') {
      setData(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await loadChannels(org.id);
      setData(response);
    } catch (requestError) {
      setData(null);
      if (requestError.status === 404) {
        setError(
          'Upload channel status is not available on this API yet (missing GET …/ingest-channels on Provider Admin). Deploy the latest evault-backend to UAT, then refresh. SFTP still requires DevOps to set INGEST_SFTP_INBOX_ROOT and create the PAYZEN_WF folder on the mount.'
        );
      } else {
        setError(requestError.message || 'Could not load upload channels.');
      }
    } finally {
      setLoading(false);
    }
  }, [org?.id, org?.org_type, loadChannels]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!org || org.org_type !== 'originator') {
    return (
      <div className="mt-4 rounded-xl border border-eorig-border bg-eorig-surface px-4 py-3 text-sm text-eorig-muted">
        SFTP and file-share inboxes are provisioned for <strong>Originator</strong> organisations
        only. Custodian and Investor orgs do not receive a drop folder.
      </div>
    );
  }

  const inboxChannels = (data?.channels || []).filter((row) =>
    ['SFTP', 'FILE_SHARE'].includes(row.channel)
  );

  return (
    <div className="mt-4 space-y-3 rounded-xl border border-eorig-border bg-eorig-surface px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold text-eorig-ink">Document drop paths (SFTP / file share)</h3>
          <p className="mt-1 text-[12px] text-eorig-muted">
            One inbox per originator organisation. The folder name is this org&apos;s code (
            <span className="font-mono">{org.code}</span>). Originator users import from here in New
            Submission; custodians assigned to this org&apos;s programs use the same paths on Upload
            batch.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          className="text-[12px] font-semibold text-[#003A70] hover:underline disabled:opacity-40"
        >
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <p className="text-eorig-muted">Loading channel status…</p>
      ) : null}
      {error ? (
        <p className="text-rose-600" role="alert">
          {error}
        </p>
      ) : null}

      {inboxChannels.length === 0 && data && !loading ? (
        <p className="text-eorig-muted">
          Inbox channels are not enabled on this environment (DevOps: set{' '}
          <span className="font-mono">INGEST_SFTP_INBOX_ROOT</span> /{' '}
          <span className="font-mono">INGEST_FILE_SHARE_ROOT</span>).
        </p>
      ) : null}

      {inboxChannels.map((row) => (
        <div
          key={row.channel}
          className="rounded-lg border border-eorig-border bg-white px-3 py-2"
        >
          <p className="font-medium text-eorig-ink">{row.label}</p>
          <p className="mt-1 font-mono text-[12px] text-eorig-ink break-all">
            {row.inbox_path || `${org.code}/ (mount not configured)`}
          </p>
          <p className="mt-1 text-[12px] text-eorig-muted">
            {row.enabled
              ? row.inbox_ready
                ? `Ready · ${row.file_count ?? 0} file(s) waiting`
                : 'Folder not provisioned on the server — ask DevOps to create this directory.'
              : 'Channel disabled in this environment.'}
          </p>
        </div>
      ))}

      {data?.allowed_extensions?.length ? (
        <p className="text-[11px] text-eorig-muted">
          Allowed extensions: {data.allowed_extensions.join(', ')} · max{' '}
          {Math.round((data.max_file_bytes || 0) / (1024 * 1024))} MB per file
        </p>
      ) : null}
    </div>
  );
}
